import React from 'react';
import styled from '@emotion/styled';
import CloseIcon24 from 'assets/svg/24-close-bolder.svg';
import { MovableMap } from 'components/movable-map';
import { OverlayWrapper } from 'components/overlay-wrapper';
import { colorToAlpha } from 'helpers/insight';
import { useScreenBreakpoint } from 'consts/breakpoints';


interface IMapDialogProps {
  onClose: () => void;
  address: string;
  showOnlyMap: boolean;
  hideCloseBtn?: boolean;
  favoriteToggler: React.ReactNode;
  shareButton: React.ReactNode;
}

const Container = styled.div({
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  position: 'fixed',
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  '&&& .mapboxgl-ctrl-bottom-right': {
    right: 0,
  },
});

const MapContainer = styled.div(({ theme }) => ({
  padding: theme.spacing(2, 10, 8, 10),
  flexGrow: 1,
  display: 'flex',
}));

const MapInnerContainer = styled.div(({ theme }) => ({
  position: 'relative',
  flexGrow: 1,
  '& .mapboxgl-canvas': {
    borderRadius: theme.borderRadius.small,
  },
}));

const ControlsRow = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 5),
  alignItems: 'center',
}));

const ButtonsContainer = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

const AddressText = styled.div(({ theme }) => ({
  color: theme.colors.neutrals.white,
  fontSize: 21,
  fontWeight: 500,
}));

const CloseButton = styled.button(({ theme }) => ({
  background: 'none',
  padding: theme.spacing(1),
  margin: -theme.spacing(1),
  border: 'none',
  cursor: 'pointer',
}));

const MobileCloseButton = styled.button<{ position: 'left' | 'right' }>(({ theme, position }) => ({
  position: 'absolute',
  zIndex: 99,
  padding: theme.spacing(1),
  top: theme.spacing(position === 'left' ? 3 : 2),
  [position]: theme.spacing(3),
  borderRadius: theme.borderRadius.round,
  background: colorToAlpha(theme.colors.neutrals.grey1, 0.7),
  border: 'none',
  cursor: 'pointer',
  'svg': {
    display: 'block',
  },
}));

export const MapDialog: React.FC<IMapDialogProps> = (props) => {
  const { onClose, address, favoriteToggler, shareButton, showOnlyMap, hideCloseBtn } = props;
  const breakpoints = useScreenBreakpoint();

  if (showOnlyMap) {
    return (
      <OverlayWrapper>
        <Container>
          {hideCloseBtn ? null : (
            <MobileCloseButton position={breakpoints < 3 ? 'left' : 'right'} onClick={onClose}>
              <CloseIcon24 />
            </MobileCloseButton>
          )}
          <MovableMap />
        </Container>
      </OverlayWrapper>
    );
  }

  return (
    <OverlayWrapper>
      <Container>
        <ControlsRow>
          <CloseButton onClick={onClose}>
            <CloseIcon24 />
          </CloseButton>
          <AddressText>
            {address}
          </AddressText>
          <ButtonsContainer>
            {shareButton}
            {favoriteToggler}
          </ButtonsContainer>
        </ControlsRow>
        <MapContainer>
          <MapInnerContainer>
            <MovableMap />
          </MapInnerContainer>
        </MapContainer>
      </Container>
    </OverlayWrapper>
  );
};
